import {
    Alert,
    Box,
    FormControl,
    Grid,
    Heading,
    Text,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
    ToggleButton,
} from "@caspeco/casper-ui-library";
// eslint-disable-next-line caspeco/discourage-chakra-import
import { InputGroup } from "@chakra-ui/react";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { SafeHTML } from "components/safe-html";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { log, trackEvent } from "logger/logger";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Routes } from "routes";

function trackLoadingTime() {
    if ("__APP_START_TIME__" in window && "__IS_TRACKED__" in window && !window.__IS_TRACKED__) {
        window.__IS_TRACKED__ = true;
        const now = new Date();
        const loadTime = now.getTime() - window.__APP_START_TIME__.getTime();
        log("App load time: ", loadTime);
        trackEvent("AppLoadTime", {
            ms: loadTime,
        });
    }
}
export const SelectGuests = () => {
    const state = useBookingStateContext();
    const { setGuests } = useBookingActionsContext();
    const { guests: guestsState } = state;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const externalBookingSettings = useExternalBookingSettings().data;
    const { maxWebGuests } = externalBookingSettings?.unitsMetaData ?? { maxWebGuests: 0 };
    const activateInquiry = externalBookingSettings?.activateInquiry ?? false;

    const introductionMessageHeader = externalBookingSettings?.introductionMessageHeader;
    const introductionMessage = externalBookingSettings?.introductionMessage;

    // Track guest selection step
    useStepTracking({
        stepName: TrackingStepName.GuestStep,
        autoTrack: true,
    });

    useEffect(trackLoadingTime, []);

    const handleBookingRequestRedirect = () => {
        // Navigate to booking request flow
        const bookingRequestPath = `${location.pathname}/${Routes.BOOKING_REQUEST_INTRO}`;
        void navigate({
            pathname: bookingRequestPath,
            search: location.search,
        });
    };

    return (
        <FormControl key="guest-choice" id="guestChoice">
            {introductionMessageHeader && (
                <Heading as="h1" fontSize={ThemeFontSizeVariable.X2Large}>
                    {introductionMessageHeader}
                </Heading>
            )}
            {introductionMessage && (
                <Text mt={ThemeSpaceVariable.Medium} mb={ThemeSpaceVariable.Large}>
                    <SafeHTML
                        element="span"
                        options={{
                            allowedTags: ["a", "br"],
                            allowedSchemes: ["tel", "mailto", "https"],
                        }}
                        html={introductionMessage}
                    />
                </Text>
            )}

            <InputGroup>
                <Grid templateColumns="repeat(4, 1fr)" gap={ThemeSpaceVariable.Medium} width="100%">
                    {[...Array(maxWebGuests + 1).keys()]
                        .map((n) => n + 1)
                        .map((guests) => {
                            const isLastButton = guests === maxWebGuests + 1;
                            const isBookingRequest = isLastButton && activateInquiry;
                            return (
                                <ToggleButton
                                    key={guests}
                                    size="lg"
                                    w="full"
                                    onClick={() => {
                                        if (isBookingRequest) {
                                            handleBookingRequestRedirect();
                                        } else {
                                            setGuests?.(guests);
                                            if (guests > maxWebGuests) {
                                                if (guestsState) setGuests?.(maxWebGuests);
                                                setTimeout(() => {
                                                    window.scrollTo({
                                                        behavior: "smooth",
                                                        top: window.document.body.scrollHeight,
                                                    });
                                                }, 0);
                                            }
                                        }
                                    }}
                                    h={ThemeSpaceVariable.XLarge}
                                    isPressed={Boolean(guestsState && guests === maxWebGuests + 1)}
                                    gridColumn={isBookingRequest ? "span 4" : "span 1"}
                                >
                                    {isBookingRequest
                                        ? t("bookingRequest.createRequest", {
                                              numberOfGuests: maxWebGuests + 1,
                                          })
                                        : `${guests}${isLastButton ? "+" : ""}`}
                                </ToggleButton>
                            );
                        })}
                </Grid>
            </InputGroup>
            {(guestsState ?? 0) >= maxWebGuests && !activateInquiry && (
                <Box mt={ThemeSpaceVariable.Medium}>
                    <Alert type="info" iconVerticalAlign={"flex-start"}>
                        <SafeHTML
                            className="textFromSettings"
                            element="div"
                            options={{
                                allowedTags: ["a", "br"],
                                allowedSchemes: ["tel", "mailto", "https"],
                            }}
                            html={
                                externalBookingSettings?.unitsMetaData.messageExceededGuests ?? ""
                            }
                        />
                    </Alert>
                </Box>
            )}
        </FormControl>
    );
};
